/**
 * AlliAvia - Allison Aviation Operations & Billing System
 * Entry point
 */

import { initSupabase } from './services/supabase.js';
import { initRouter, registerRoute, resetLastRenderedHash, navigate } from './services/router.js';
import { initAuth, onAuthChange } from './services/auth.js';
import { renderSidebar, renderLoginSidebar, initSidebarListeners, applyTheme } from './components/sidebar.js';
import { openFlightLogModal } from './components/flight-log-modal.js';
import { getUser } from './services/auth.js';
import { renderLogin } from './pages/login.js';
import { renderDashboard } from './pages/dashboard.js';
import { renderDefects } from './pages/defects.js';
import { renderMaintenance } from './pages/maintenance.js';
import { renderBilling } from './pages/billing.js';
import { renderSettings } from './pages/settings.js';
import { renderBookings } from './pages/bookings.js';
import { renderAircraftList } from './pages/aircraft-list-page.js';
import { renderAircraftDetail } from './pages/aircraft-detail-page.js';
import { renderProfile } from './pages/profile.js';
import { renderAdminFlights } from './pages/admin-flights.js';
import { renderAdminBilling } from './pages/admin-billing.js';
import { renderOwnerDefects } from './pages/owner-defects.js';
import { renderOwnerBookings } from './pages/owner-bookings.js';
import { renderImport } from './pages/import.js';
import { renderUserDetail } from './pages/user-detail-page.js';

// Apply saved theme immediately to avoid flash
applyTheme(localStorage.getItem('theme') || 'auto');

async function boot() {
  // ── Public route ──────────────────────────────────────────────
  registerRoute('/login', renderLogin);

  // ── Pilot, Admin, Owner (all authenticated users) ────────────
  registerRoute('/', renderDashboard);
  registerRoute('/aircraft', renderAircraftList);
  registerRoute('/aircraft/:id', renderAircraftDetail);
  registerRoute('/flights', renderBookings);
  registerRoute('/defects', renderDefects);
  registerRoute('/billing', renderBilling);
  registerRoute('/profile', renderProfile);
  registerRoute('/maintenance', renderMaintenance);

  // ── Admin + Owner ────────────────────────────────────────────
  registerRoute('/settings', renderSettings, { roles: ['admin'] });
  registerRoute('/users/:id', renderUserDetail, { roles: ['admin'] });
  registerRoute('/admin/flights', renderAdminFlights, { roles: ['admin'] });
  registerRoute('/admin/billing', renderAdminBilling, { roles: ['admin'] });
  registerRoute('/import', renderImport, { roles: ['admin'] });

  // ── Owner only ───────────────────────────────────────────────
  registerRoute('/owner/defects', renderOwnerDefects, { roles: ['owner'] });
  registerRoute('/owner/bookings', renderOwnerBookings, { roles: ['owner'] });

  // Attempt Supabase init + auth — if it fails, the router's
  // auth guard will redirect to /login on the first route resolve
  try {
    const supabase = initSupabase();
    await initAuth(supabase);
  } catch (err) {
    console.warn('Auth init failed — starting in unauthenticated mode:', err.message);
  }

  // Remove boot loader now that auth has resolved (or failed)
  const bootLoader = document.getElementById('boot-loader');
  if (bootLoader) bootLoader.remove();

  // Re-render sidebar when auth state changes (e.g. after login or logout)
  onAuthChange(({ event, session }) => {
    const sidebarEl = document.getElementById('sidebar');

    if (session?.user) {
      // Transitioning to authenticated — re-render sidebar with role-filtered links
      const path = window.location.hash.slice(1) || '/';
      const collapsed = localStorage.getItem('sidebar-collapsed') === 'true';
      if (sidebarEl) {
        sidebarEl.innerHTML = renderSidebar(path, collapsed);
        initSidebarListeners();
      }
      // Force route re-evaluation so the main viewport updates
      resetLastRenderedHash();
      window.dispatchEvent(new Event('hashchange'));
      // Update FAB visibility
      const fab = document.getElementById('log-flight-fab');
      if (fab) {
        const onLogin = window.location.hash === '#/login';
        fab.hidden = !getUser() || onLogin;
      }
    } else if (event === 'SIGNED_OUT') {
      // Transitioning to unauthenticated — show login sidebar and navigate
      if (sidebarEl) {
        sidebarEl.innerHTML = renderLoginSidebar();
        sidebarEl.classList.remove('sidebar--collapsed');
      }
      const fab = document.getElementById('log-flight-fab');
      if (fab) fab.hidden = true;
      resetLastRenderedHash();
      navigate('/login');
    }
  });

  // Render sidebar once after auth (role is now known).
  // All subsequent navigations update only the active link class.
  const collapsed = localStorage.getItem('sidebar-collapsed') === 'true';
  const sidebarEl = document.getElementById('sidebar');
  if (sidebarEl) {
    sidebarEl.innerHTML = renderSidebar('/', collapsed);
    if (collapsed) sidebarEl.classList.add('sidebar--collapsed');
    initSidebarListeners();
  }

  // Global "Log Flight" button — visible on every page for authenticated users.
  const fab = document.getElementById('log-flight-fab');
  if (fab) {
    fab.addEventListener('click', () => {
      openFlightLogModal({ onSaved: () => window.location.reload() });
    });
    const updateFabVisibility = () => {
      const onLogin = window.location.hash === '#/login';
      fab.hidden = !getUser() || onLogin;
    };
    window.addEventListener('hashchange', updateFabVisibility);
    updateFabVisibility();
  }

  initRouter();
}

boot().catch(console.error);
